import { EventEmitter } from 'events';
import { TypedEmitter } from '../util';
import { action, makeObservable, observable } from 'mobx';

export type TransitionStatus =
  | 'enter'
  | 'entering'
  | 'entered'
  | 'afterEntered'
  | 'beforeExit'
  | 'exit'
  | 'exiting'
  | 'exited';

export class TransitionState<T> {
  status: TransitionStatus;
  show: boolean;
  data: T | null | undefined;
  nextData: T | null | undefined;

  events = new EventEmitter() as TypedEmitter<
    Record<TransitionStatus, () => unknown> & {
      exitedAfterNewData: () => unknown;
      exitedAfterClickClose: () => unknown;
      dismissed: (params: { dismissMode: 'automatic' | 'manual' }) => unknown;
    }
  >;

  constructor(initialValue = false) {
    this.show = initialValue;
    this.status = initialValue ? 'entered' : 'exited';
    this.data = null;
    this.nextData = null;

    this.events.on('exited', () => {
      const { events } = this;
      events.emit('exitedAfterNewData');
      events.emit('exitedAfterClickClose');
    });

    makeObservable(this, {
      show: observable.ref,
      status: observable.ref,
      data: observable.ref,
      setShow: action,
      setStatus: action,
      clickClose: action,
      set: action.bound,
      setSync: action.bound,
    });
  }

  setShow(value: boolean) {
    this.show = value;
  }

  setStatus(status: TransitionStatus) {
    this.status = status;

    this.events.emit(status);
  }

  set(data?: T | null) {
    if (data == null) {
      this.events.emit('dismissed', { dismissMode: 'automatic' });
      this.show = false;
      return;
    }
    this.transitionToNewData(data);
  }

  // does not transition if data already exists
  setSync(data?: T | null) {
    if (this.show && data != null) {
      this.data = data;
      return;
    }

    this.transitionToNewData(data);
  }

  clickClose(onAfterClose?: () => unknown) {
    if (!this.show) {
      return;
    }

    const { events } = this;
    this.show = false;
    this.nextData = undefined;
    events.removeAllListeners('exitedAfterClickClose');
    events.once(
      'exitedAfterClickClose',
      action(() => {
        events.emit('dismissed', { dismissMode: 'manual' });
        if (!this.show && typeof this.nextData === 'undefined') {
          this.data = null;
          onAfterClose?.();
        }
      }),
    );
  }

  private transitionToNewData(data?: T | null) {
    const { events } = this;
    this.nextData = data;
    if (this.show) {
      this.show = false;
      events.removeAllListeners('exitedAfterNewData');
      events.once(
        'exitedAfterNewData',
        action(() => {
          events.emit('dismissed', { dismissMode: 'automatic' });
          if (!this.show && this.nextData === data) {
            this.show = true;
            this.data = data;
          }
        }),
      );
    } else {
      this.show = true;
      this.data = data;
    }
  }
}
